/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailureTrendCard/ControllerViews/PipelineOutcomeFailureTrendCard" />
import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { LoadingComponent } from "Analytics/Shared/Analytics-Visuals-Common/LoadingComponent/LoadingComponent";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { DonutChart, IDonutChartProps } from "Analytics/Shared/Visuals/DonutChart/Components/DonutChart";
import { PipelineReportInsights } from "Pipeline/Analytics/Common/Components/PipelineReportInsights";
import { maxItemsToShowInCharts } from "Pipeline/Analytics/Common/Constants";
import { colors } from "Pipeline/Analytics/Common/Constants";
import { StackedColumnChartHelper } from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Common/StackedColumnChartHelper";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Resources";
import {
    IPipelineOutcomeFailureTrendState,
    PipelineOutcomeFailureTrendStore
} from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Stores/PipelineOutcomeFailureTrendStore";
import * as React from "react";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";
import { localeFormat } from "VSS/Core/Util/String";
import { Chart } from "VSS/Features/Charts/Chart";
import { Datum } from "VSS/Features/Charts/Contracts";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Components/Card/Card";
import { Icon } from "VSSUI/Icon";
import { SpinnerSize } from "VSSUI/Spinner";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface IPipelineOutcomeFailureTrendCardProps extends IVssComponentProperties {
    store: PipelineOutcomeFailureTrendStore;
    headerClassName?: string;
    onDataRendered?: () => void;
    errorText?: string;
}

enum WidgetCharts {
    Chart_FailureTrend="Chart_FailureTrend"
}

export class PipelineOutcomeFailureTrendCard extends VssComponent<IPipelineOutcomeFailureTrendCardProps, IPipelineOutcomeFailureTrendState> {
    private stackedChartHelper: StackedColumnChartHelper;
    private _onDataRenderedCalled = false;
    private _pipelineReportInsights: PipelineReportInsights;
    private isChartClicked = {
        [WidgetCharts.Chart_FailureTrend]: false
    };
    constructor(props: IPipelineOutcomeFailureTrendCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<IPipelineOutcomeFailureTrendState>;
        this.stackedChartHelper = new StackedColumnChartHelper(this.context.pageContext);
        this._pipelineReportInsights = new PipelineReportInsights();
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IPipelineOutcomeFailureTrendCardProps, prevState: IPipelineOutcomeFailureTrendState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render(): JSX.Element | null {
        let title = Resources.CardTitle;
        if (
            this.state.data &&
            this.state.data.pipelineOutcomePassRateData.failurePerStage &&
            this.state.data.pipelineOutcomePassRateData.failurePerStage.length > 1
        ) {
            title = Resources.CardTitleMultiStage;
        }
        if (this.state.data && this.state.data.pipelineOutcomePassRateData.failedCount === 0) {
            return null;
        }
        return (
            <Card className={this.props.className} titleProps={{ text: title, ariaLevel: 2 }} headerClassName={this.props.headerClassName}>
                {this._getElementToDisplay()}
            </Card>
        );
    }

    private _getElementToDisplay(): JSX.Element {
        if (this.state.errorText) {
            return <ErrorComponent className="pipeline-outcome-failuretrend-container flex-grow" errorMessage={this.state.errorText} />;
        }
        if (this.state.confValues != null && this.state.data != null) {
            const trendChartData = this.state.data.trendChartData;
            const donutChartData = this.state.data.pipelineOutcomePassRateData.failurePerStage;
            const columnChartOptions: ChartProps | undefined = this.stackedChartHelper.getChartOptions(trendChartData);
            columnChartOptions.click = () => {
                if(!this.isChartClicked[WidgetCharts.Chart_FailureTrend]){
                    this.publishWidgetChartClickTelemetry(WidgetCharts.Chart_FailureTrend);
                }
                this.isChartClicked[WidgetCharts.Chart_FailureTrend] = true;
            }

            return (
                <div className="pipeline-outcome-failuretrend-container flex-grow">
                    <div className="flex-row flex-wrap">
                        <div className="failure-trend-summary flex-column">
                            <div className="flex-column flex-center flex-grow">
                                {donutChartData && donutChartData.length > 1 && <DonutChart {...this._getPieChartProps()} />}
                            </div>

                            {this.state.topFailingStageAndTask && this.state.topFailingTaskPercentage > 0 && (
                                <div className="failure-trend-metrics-insight flex-row flex-end font-size-m font-weight-normal secondary-text">
                                    <div className="flex-self-start">
                                        <Icon
                                            className="failure-trend-metric-insight-icon icon-margin font-size-ml"
                                            iconName="CRMCustomerInsightsApp"
                                        />
                                    </div>
                                    <span
                                        className="failure-trend-metrics-insight-text"
                                        dangerouslySetInnerHTML={{
                                            __html: filterXSS(
                                                this._pipelineReportInsights._getTopFailingStageAndTaskDisplayName(
                                                    this.state.topFailingStageAndTask,
                                                    this.state.topFailingTaskPercentage
                                                ),
                                                xssStyleDefaultOptions
                                            )
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {columnChartOptions && (
                            <div className="failure-trend-chart">
                                <Chart {...columnChartOptions} />
                            </div>
                        )}
                    </div>
                </div>
            );
        } else {
            return <LoadingComponent className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />;
        }
    }

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
    }

    private _getPieChartProps(): IDonutChartProps {
        const props: IDonutChartProps = {
            chartData: this._getDonutChartData(),
            pageContext: this.context.pageContext,
            enableLegends: true,
            chartInnerSize: 80,
            className: "donut-chart",
            customizeTooltip: value => `${value.toLocaleString()}%`
        };
        return props;
    }

    private _getDonutChartData(): Datum[] {
        const chartData: Datum[] = [];
        let failurePerStage;
        if (this.state.data) {
            failurePerStage = this.state.data.pipelineOutcomePassRateData.failurePerStage;
        }
        if (failurePerStage) {
            const totalStageFailures = failurePerStage.map(i => i.dataCount).reduce((a, b) => a + b);
            let otherStageCount = 0,
                otherStageFailures = 0;
            for (let index = 0, len = failurePerStage.length; index < len; index++) {
                if (index < maxItemsToShowInCharts) {
                    const data: Datum = {
                        name: failurePerStage[index].name,
                        y: getPercentage(totalStageFailures, failurePerStage[index].dataCount)
                    };
                    chartData.push(data);
                } else {
                    otherStageCount++;
                    otherStageFailures += failurePerStage[index].dataCount;
                }
            }
            if (otherStageCount > 0) {
                const data: Datum = {
                    name: localeFormat(Resources.OtherStagesWithCountText, otherStageCount),
                    y: getPercentage(totalStageFailures, otherStageFailures)
                };
                chartData.push(data);
            }
            chartData.sort((a: Datum, b: Datum) => {
                return b.y! - a.y!;
            });
            for (let index = 0, len = chartData.length; index < len; index++) {
                chartData[index].color = colors[index];
            }
        }
        return chartData;
    }

    private publishWidgetChartClickTelemetry = (chartType: string) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("PipelineAnalytics", "WidgetChartClick", {
            Chart: chartType
        })
    }
}
